/**
 * @typedef {object} macPongCategoryStat
 * @property {string} macPongWinrate - Winrate
 * @property {string} macPongGamesCount - Games count
 * @property {string} macPongGamesWins - Games wins
 * @property {string} macPongGameLosses - Games losses
 * @property {string} macPongAverageTimeGame - Average game time
 * @property {string} macPongBallBounceCount - Ball bounces
 * @property {string} macPongAverageTimeScore - Average time to score
 */

/**
 * @typedef {object} atariPongCategoryStat
 * @property {string} atariPongBallBouncesCount - Ball bounces
 * @property {string} atariPongPointsScored - Points scored
 * @property {string} atariPongGameAgainstBot - Games against bot
 * @property {string} atariPongGamesAgainstHuman - Games against player
 * @property {string} atariPongTimePerGameAgainstBot - Average game time against bot
 * @property {string} atariPongTimePerGameAgainstHuman - Average game time against player
 * @property {string} atariPongAverageTimeScoreAgainstBot - Average time to score against bot
 * @property {string} atariPongAverageTimeScoreAgainstHuman - Average time to score against player
 */

/**
 * @typedef {object} tetrisCategoryStat
 * @property {string} tetrisHighScore - High score
 * @property {string} tetrisAverageScore - Average score
 * @property {string} tetrisTetrominosPlaced - Number of tetrominos placed
 * @property {string} tetrisTetrisCount - Number of Tetris
 * @property {string} tetrisAverageTetrisPerGame - Average Tetris per game
 * @property {string} tetrisAverageLinesClearedPerClear - Average lines cleared
 * @property {string} tetrisLinesCleared - Total lines cleared
 * @property {string} tetrisTetrominosRotate - Number of tetrominos rotated
 */

/**
 * @typedef {object} pacmanCategoryStat
 * @property {string} pacmanHighScore - High score
 * @property {string} pacmanAverageScore - Average score
 * @property {string} pacmanGhostsEaten - Number of ghosts eaten
 * @property {string} pacmanAverageGhostsEaten - Average ghosts eaten
 * @property {string} pacmanFruitsEaten - Number of fruits eaten
 * @property {string} pacmanAverageFruitsEaten - Average fruits eaten
 * @property {string} pacmanDeathsCount - Deaths count
 */

/**
 * @typedef {object} allCategoryStat
 * @property {macPongCategoryStat} macPong - Mac Pong stats
 * @property {atariPongCategoryStat} atariPong - Atari Pong stats
 * @property {tetrisCategoryStat} tetris - Tetris stats
 * @property {pacmanCategoryStat} pacman - Pacman stats
 */

/**
 * @typedef {'macPong' | 'atariPong' | 'tetris' | 'pacman'} statCategory
 */

import LocalStats from "../../serverConnection/LocalStats.js";

const MAC_PONG_KEYMAP = {
    WinRate:        'Winrate',
    NbrOfGames:     'GamesCount',
    NbrOfWins:      'GamesWins',
    NbrOfLosses:    'GameLosses',
    AvgTimePerGame: 'AverageTimeGame',
    NbrOfBounces:   'BallBounceCount',
    AvgTimeToScore: 'AverageTimeScore'
}

const PACMAN_KEYMAP = {
    HighScore:              'HighScore',
    AvgScore:               'AverageScore',
    NbrOfGhostsEaten:       'GhostsEaten',
    AvgGhostsEatenPerGame:  'AverageGhostsEaten',
    NbrOfFruitsEaten:       'FruitsEaten',
    AvgFruitsEatenPerGame:  'AverageFruitsEaten',
    NbrOfDeaths:            'DeathsCount'
}

const TETRIS_KEYMAP = {
    HighScore:              'HighScore',
    AvgScore:               'AverageScore',
    NbrOfTetris:            'TetrisCount',
    AvgTetrisPerGame:       'AverageTetrisPerGame',
    NbrOfLinesCleared:      'LinesCleared',
    AvgLinesClearedPerClear:'AverageLinesClearedPerClear',
    NbrOfTetriminosPlaced:  'TetrominosPlaced',
    NbrOfTetrominosRotate:  'TetrominosRotate'
}

const ATARI_PONG_KEYMAP = {
    NbrOfBounces:            'BallBouncesCount',
    NbrOfPointsScored:       'PointsScored',
    NbrOfSoloGames:          'GameAgainstBot',
    NbrOf1v1Games:           'GamesAgainstHuman',
    AvgTimePerSoloGame:      'TimePerGameAgainstBot',
    AvgTimePer1v1Game:       'TimePerGameAgainstHuman',
    AvgTimeToScoreSolo:      'AverageTimeScoreAgainstBot',
    AvgTimeToScore1v1:       'AverageTimeScoreAgainstHuman'
}

class StatView {
    #handleLocalStatsUpdateBind = this.#handleLocalStatsUpdate.bind(this);

    /**
     * @author Corentin (ccharton) Charton
     * Edited by: Sonam (scrumier) Crumiere
     * @description Constructor for the StatView class. This is a public deployment:
     * stats are computed by the games client-side and kept in LocalStats
     * (localStorage), never on a server, so there is no websocket push to wait for
     * any more, just a local event fired whenever LocalStats changes.
     */
    constructor() {

        const mutationObserver = new MutationObserver((_, observer) => {
            const root = document.getElementById('statTabCenterMenu');
            if (!root) {
                return;
            }
            this.injectAllCategories(LocalStats.getStats())
            observer.disconnect();
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });

        window.addEventListener('localStatsUpdated', this.#handleLocalStatsUpdateBind)
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Re-inject the stats into the view whenever LocalStats changes
     * @param event {CustomEvent} - The local stats update event
     */
    #handleLocalStatsUpdate(event) {
        if (document.getElementById('statTabCenterMenu')) {
            this.injectAllCategories(event.detail.stats)
        }
    }

    /**
     * @author Corentin (ccharton)
     * @description Inject a stat value into an existing DOM element
     * @param {string} id - The id of the stat container
     * @param {string|number} value - The value to inject
     */
    injectStatValue(id, value) {
        const block = document.getElementById(id)
        if (!block) {
            return
        }

        const statElem = block.querySelector('.statTextBlockStat')
        if (!statElem) {
            return
        }
        if (id === 'macPongWinrate') {
            statElem.textContent = value + '%'
        } else if (id.includes('Time')) {
            statElem.textContent = value + 's'
        } else {
            statElem.textContent = value
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Inject a stat value into an existing DOM element
     * @param categoryStat {macPongCategoryStat | atariPongCategoryStat | tetrisCategoryStat | pacmanCategoryStat} - Stat of the category
     * @param prefix {string} - Prefix of the stat DOM id
     * @param keymap {object} - Keymap to map the stat keys to the DOM ids
     */
    injectCategory(categoryStat, prefix, keymap = {}) {
        if (!document.getElementById('statTabCenterMenu')) {
            return
        }
        for (const [key, value] of Object.entries(categoryStat)) {
            const domSuffix = keymap[key] || key
            this.injectStatValue(prefix + domSuffix, value)
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Clear the stats of a category
     * @param category {statCategory} - Category to clear
     */
    clearCategory(category) {
        const statBlock = document.getElementById(`${category}ListContent`)
        if (statBlock) {
            statBlock.querySelectorAll('.statTextBlockStat').forEach(el => {
                el.textContent = ''
            });
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Inject all categories stats
     * @param allStats {allCategoryStat} - All categories stats
     */
    injectAllCategories(allStats) {
        const prefix = {
            pong:      'macPong',
            AtariPong: 'atariPong',
            tetris:    'tetris',
            pacman:    'pacman',
        }

        for (const [category, stats] of Object.entries(allStats)) {
            const pre = prefix[category]
            if (!pre) {
                continue;
            }
            const map =
                category === 'pong'     ? MAC_PONG_KEYMAP :
                category === 'pacman'   ? PACMAN_KEYMAP :
                category === 'tetris'   ? TETRIS_KEYMAP :
                category === 'AtariPong' ? ATARI_PONG_KEYMAP :
                {}
            this.injectCategory(stats, pre, map)
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Reset the stat view
     */
    reset() {
        this.clearCategory('atariPong')
        this.clearCategory('macPong')
        this.clearCategory('tetris')
        this.clearCategory('pacman')

        const mutationObserver = new MutationObserver((_, observer) => {
            const root = document.getElementById('statTabCenterMenu');
            if (!root) {
                return;
            }
            this.injectAllCategories(LocalStats.getStats())
            observer.disconnect();
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });
    }
}

export default new StatView()